import { AllFlyerFilters } from '@/graphql/types';
import { useContext, useEffect, useMemo, useState } from 'react';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import { useFlyerboardPagination } from './useFlyerboardPagination';

interface ExpandedCommunityFlyerboardPaginationProps {
  flyerboardFilters: AllFlyerFilters;
  flyerType?: string;
  districtId?: string | null;
  owningIds?: string[];
  shouldUpdateFacets?: boolean;
  skip?: boolean;
  sortBy?: string;
}

export function useExpandedCommunityFlyerboardPagination(
  props: ExpandedCommunityFlyerboardPaginationProps
) {
  const {
    flyerboardFilters,
    flyerType,
    districtId,
    owningIds,
    shouldUpdateFacets = true,
    skip = false,
    sortBy,
  } = props;
  const { community: userCommunity } = useContext(UserPreferencesContext);
  const { lat, lng } = userCommunity?.location || {};
  const [shouldLoadExpandedFlyers, setShouldLoadExpandedFlyers] = useState(false);

  const {
    flyers,
    loading,
    fetchMoreFlyers: fetchMoreRegularFlyers,
    total,
    isPaginationEnd,
  } = useFlyerboardPagination({
    flyerboardFilters,
    flyerType,
    districtId,
    owningIds,
    shouldUpdateFacets,
    skip,
    sortBy,
  });

  const expandedFlyerboardFilters = useMemo<AllFlyerFilters>(
    () => ({
      ...flyerboardFilters,
      communityLocation: userCommunity?.location ? { lat, lng } : undefined,
      communityRadius: userCommunity?.distance ? userCommunity.distance : undefined,
    }),
    [flyerboardFilters, userCommunity, lat, lng]
  );

  const {
    flyers: expandedFlyers,
    loading: expandedLoading,
    fetchMoreFlyers: fetchMoreExpandedFlyers,
    total: expandedTotal,
    isPaginationEnd: isExpandedPaginationEnd,
  } = useFlyerboardPagination({
    flyerboardFilters: expandedFlyerboardFilters,
    flyerType,
    districtId,
    owningIds,
    shouldUpdateFacets,
    skip: skip || !shouldLoadExpandedFlyers,
    sortBy,
  });

  useEffect(() => {
    if (skip) return;
    setShouldLoadExpandedFlyers(false);
    if (isPaginationEnd) {
      setShouldLoadExpandedFlyers(true);
    }
  }, [flyerboardFilters, isPaginationEnd, skip]);

  const combinedFlyers = useMemo(() => {
    const allFlyers = [...flyers, ...expandedFlyers];
    return allFlyers.filter(
      (flyer, index, self) => index === self.findIndex(f => f.flyerId === flyer.flyerId)
    );
  }, [flyers, expandedFlyers]);

  const combinedLoading = loading || (shouldLoadExpandedFlyers && expandedLoading);

  const combinedIsPaginationEnd =
    isPaginationEnd && (!shouldLoadExpandedFlyers || isExpandedPaginationEnd);

  const combinedTotal = combinedIsPaginationEnd ? combinedFlyers.length : total + expandedTotal;

  const fetchMoreFlyers = () => {
    if (!isPaginationEnd) {
      fetchMoreRegularFlyers();
    } else if (shouldLoadExpandedFlyers && !isExpandedPaginationEnd) {
      fetchMoreExpandedFlyers();
    }
  };

  return {
    flyers: combinedFlyers,
    loading: combinedLoading,
    fetchMoreFlyers,
    total: combinedTotal,
    isPaginationEnd: combinedIsPaginationEnd,
  };
}
